import { EmptyStateTable } from '@components'

import { Color, Text } from '@billy/design-system'
import { Table } from '@shinetools/lumen-react'
import { ReactElement, ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { EXPERT_TRIAL_BALANCE_TABLE_MIN_WIDTH } from '../../constants/expertTrialBalanceColumnWidths'
import { useExpertTrialBalanceColumns } from '../../hooks/useExpertTrialBalanceColumns'
import type { ExpertTrialBalanceTableProps } from '../../types/expertTrialBalanceView'
import { getVisibleTrialBalanceColumns } from '../../utils/getVisibleTrialBalanceColumns'
import { sumVisibleTrialBalanceRows } from '../../utils/sumVisibleTrialBalanceRows'
import { ExpertTrialBalanceSearchEmptyState } from '../ExpertTrialBalanceSearchEmptyState'
import { TotalsRow } from './elements/TotalsRow'
import * as Styled from './styles'

export const ExpertTrialBalanceTable = ({
  hiddenColumns = [],
  isError,
  isLoading,
  rows,
  searchTerm = '',
  totals,
}: ExpertTrialBalanceTableProps): ReactElement => {
  const { t } = useTranslation()
  const allColumns = useExpertTrialBalanceColumns(searchTerm)
  const columns = useMemo(() => getVisibleTrialBalanceColumns(allColumns, hiddenColumns), [allColumns, hiddenColumns])
  const hasData = rows.length > 0
  const isSearchActive = searchTerm.trim().length > 0
  const displayTotals = isSearchActive && hasData ? sumVisibleTrialBalanceRows(rows) : totals

  const tableHeader = (
    <Table.Header>
      <Table.Row>
        {columns.map((column) => (
          <Table.HeaderCell align={column.align} key={column.id} width={column.width}>
            {column.header}
          </Table.HeaderCell>
        ))}
      </Table.Row>
    </Table.Header>
  )

  const renderEmptyContent = (): ReactNode => {
    if (isError) {
      return (
        <Text alignment="center" color={Color.Grey800}>
          {t('expert_trial_balance.error.load_failed')}
        </Text>
      )
    }

    if (isSearchActive) {
      return <ExpertTrialBalanceSearchEmptyState text={t('expert_trial_balance.empty_state.no_accounts_search')} />
    }

    return (
      <Text alignment="center" color={Color.Grey800}>
        {t('expert_trial_balance.empty_state.no_accounts')}
      </Text>
    )
  }

  const renderTableBody = (): ReactElement => (
    <Table.Body>
      {rows.map((row) => (
        <Table.Row key={row.id}>
          {columns.map((column) => (
            <Table.Cell align={column.align} highlighted={column.highlighted} key={column.id}>
              {column.Cell(row)}
            </Table.Cell>
          ))}
        </Table.Row>
      ))}
    </Table.Body>
  )

  if (!hasData && !isLoading) {
    return (
      <EmptyStateTable header={tableHeader} minWidth={EXPERT_TRIAL_BALANCE_TABLE_MIN_WIDTH}>
        {renderEmptyContent()}
      </EmptyStateTable>
    )
  }

  return (
    <Styled.TrialBalanceTableWrapper>
      <Table compact>
        <Table.Content maxHeight="none" scrollable style={{ minWidth: EXPERT_TRIAL_BALANCE_TABLE_MIN_WIDTH }}>
          {tableHeader}
          {!isLoading ? renderTableBody() : <Table.Skeleton columns={columns.length} />}
          {!isLoading && hasData && (
            <Table.Foot>
              <TotalsRow columns={columns} totals={displayTotals} />
            </Table.Foot>
          )}
        </Table.Content>
      </Table>
    </Styled.TrialBalanceTableWrapper>
  )
}
